<template>
  <div v-if="embed"><slot /></div>
  <div
    v-else
    :class="[
      _.page,
      whiteBack && _.whiteBack,
      onlyBack && _.onlyBack,
      whiteBackground && _.whiteBackground,
      (transparentHeader || classical) && _.transparentHeader,
      noHeaderShadow && _.noHeaderShadow,
      titleAdaptive && _.titleAdaptive,
      !noSafeArea && _.safeArea,
      loading && _.loading,
      ($slots.header || $scopedSlots.header) && _.customHeader,
    ]"
    :style="{
      ...(customBackground && !loading
        ? { backgroundColor: customBackground }
        : {}),
      '--real-page-header-height':
        transparentHeader || classical ? 'var(--page-header-height)' : '0px',
    }"
  >
    <div
      v-if="
        ($state.inDcb || $state.inLove) &&
        !((transparentHeader || classical) && loading)
      "
      :class="[_.hd, '__page_header__']"
      :style="{
        backgroundColor:
          classical && classicalScrollBackgroundColor
            ? !headerParallaxRate
              ? 'transparent'
              : classicalScrollBackgroundColor
            : headerBackgroundColorWhenScroll
            ? !headerParallaxRate
              ? 'transparent'
              : headerBackgroundColorWhenScroll
            : classical
            ? !headerParallaxRate
              ? 'transparent'
              : `rgba(255, 255, 255, ${headerParallaxRate})`
            : headerBackgroundColor || undefined,
        backgroundImage:
          (headerBackgroundImage && `url('${headerBackgroundImage}')`) ||
          undefined,
      }"
    >
      <slot
        name="header"
        :headerParallaxRate="headerParallaxRate"
        :handleBackClick="handleBackClick"
        :pageHeaderHeight="pageHeaderHeight"
      >
        <x-icon
          v-show="!noBack"
          name="back"
          :class="[_.back, '__page_back__']"
          :style="
            classical
              ? {
                  color:
                    classical && classicalScrollBackgroundColor
                      ? 'white'
                      : !headerParallaxRate
                      ? 'white'
                      : `rgba(0, 0, 0, ${headerParallaxRate})`,
                }
              : {}
          "
          @click="handleBackClick"
        />
        <slot name="left" />
        <div :class="_.grow"></div>
        <div
          :class="[_.title, _._ellipsis, '__page_title__']"
          :style="
            classical
              ? !headerParallaxRate
                ? {
                    visibility: 'hidden',
                  }
                : {
                    color: classicalScrollBackgroundColor
                      ? 'white'
                      : `rgba(0, 0, 0, ${headerParallaxRate})`,
                  }
              : {}
          "
        >
          <template v-if="title">{{ title }}</template>
          <slot v-else name="title" />
        </div>
        <div :class="_.extra">
          <slot name="extra">
            <x-icon
              v-if="share.title"
              v-track="{
                name: '分享',
                id: '0e09202c-7927-454e-b343-0783a728f3c3',
              }"
              :style="
                classical
                  ? {
                      color:
                        classical && classicalScrollBackgroundColor
                          ? 'white'
                          : !headerParallaxRate
                          ? 'white'
                          : `rgba(0, 0, 0, ${headerParallaxRate})`,
                    }
                  : {}
              "
              name="share"
              :class="_.share"
              @click="handleShareClick"
            />
          </slot>
        </div>
      </slot>
    </div>
    <div :class="_.placeholder" />
    <div :class="_.bd">
      <div v-if="$globalModel.state.isAccessDenied">
        <x-empty
          :message="
            $globalModel.state.fromTV
              ? `请使用当前TV端登录的多彩宝账号（${$globalModel.state.fromUser}）扫码查询`
              : '您无权限访问该页面'
          "
        />
      </div>
      <div v-else-if="loading" :class="_.loading">
        <x-lottie :data="pageLoadingData" :class="_.icon" />
        <div :class="_.tips">加载中...</div>
      </div>
      <div v-else ref="container" :class="[_.container, '__page_container__']">
        <slot />
        <div :class="_.tabbarHolder" />
      </div>
    </div>
  </div>
</template>

<script>
  import loveDcb from '@/utils/love.js'
  import pageLoadingData from '../page_loading.json'
  import safeAreaInsets from 'safe-area-insets'
  import Vue from 'vue'
  import { bindEvent, clamp, memoize } from 'vtils'
  import { dcb } from '@dcb/jssdk'
  import { globalModel } from '@/model'
  import { logg, pxToRem, setRootCssVar } from '@/utils'
  import { navigateBack } from '@/router'
  import { pageBridge } from '../page_bridge'
  import { wechat } from '@/wechat'
  const initCssVar = memoize(async () => {
    const env = await dcb.getEnv()
    let statusBarHeight
    if (globalModel.state.inLove) {
      statusBarHeight = loveDcb.getStatusBarHeight
    } else {
      statusBarHeight = dcb.getStatusBarHeight
    }
    if (!env.dcb && !globalModel.state.inLove) {
      setRootCssVar('status-bar-height', '0px')
    } else {
      // 苹果端：iPhone X 之后强制设为 36，之前设为 20
      if (env.ios) {
        const { height } = await statusBarHeight().catch(() => ({}))
        setRootCssVar(
          'status-bar-height',
          `${
            height
              ? `${height}px`
              : `${pxToRem(window.screen.height > 800 ? 36 : 20)}rem`
          }`,
        )
      } else {
        const { height } = await statusBarHeight().catch(() => ({}))
        if (height != null) {
          setRootCssVar(
            'status-bar-height',
            `${
              height > 50 ? height / (window.devicePixelRatio || 2) : height
              // 安卓客户端 TAB 打开获取到的高度超大，限定最大值为 36
              // Math.min(height, 36)
            }px`,
          )
        }
      }
    }

    if (!globalModel.state.inDcb && !globalModel.state.inLove) {
      setRootCssVar('page-header-height', '0px')
    }

    if (env.ios) {
      setRootCssVar('bold-value', 'bold')
    }

    setTimeout(() => {
      // 必须延时获取，否则会得到 0
      setRootCssVar('safe-area-height', `${safeAreaInsets.bottom}px`)
    }, 60)
  })

  export default Vue.extend({
    mixins: [globalModel.mixin],

    props: {
      // 标题，也可通过 slot="title" 设置
      title: {
        type: String,
        default: '',
      },

      // 返回箭头是否白色
      whiteBack: {
        type: Boolean,
        default: false,
      },

      // 是否仅显示返回箭头
      onlyBack: {
        type: Boolean,
        default: false,
      },

      noBack: {
        type: Boolean,
        default: false,
      },

      // 是否白色背景，默认灰色背景
      whiteBackground: {
        type: Boolean,
        default: false,
      },

      // 自定义背景色
      customBackground: {
        type: String,
        default: '',
      },

      // 头部背景图片
      headerBackgroundImage: {
        type: String,
        default: '',
      },

      // 是否不显示头部阴影
      noHeaderShadow: {
        type: Boolean,
        default: false,
      },

      // 标题是否自适应
      titleAdaptive: {
        type: Boolean,
        default: false,
      },

      // 头部是否透明
      transparentHeader: {
        type: Boolean,
        default: false,
      },

      // 启动滚动视差
      enableHeaderParallax: {
        type: Boolean,
        default: false,
      },

      // 获取头部滚动视差背景色
      getHeaderParallaxBackground: {
        type: Function,
        default: null,
      },

      // 是否加载中
      loading: {
        type: Boolean,
        default: false,
      },

      // 是否不启用安全区兼容
      noSafeArea: {
        type: Boolean,
        default: false,
      },

      // 滚动容器的选择器
      scrollContainerSelector: {
        type: String,
        default: '.__page_container__',
      },

      // 是否内嵌入其他页面
      embed: {
        type: Boolean,
        default: false,
      },

      // 是否经典模式
      classical: {
        type: Boolean,
        default: false,
      },

      // 经典模式下滑动时的背景颜色
      classicalScrollBackgroundColor: {
        type: String,
        default: '',
      },

      headerBackgroundColorWhenScroll: {
        type: String,
        default: '',
      },

      closeOnBack: {
        type: Boolean,
        default: false,
      },

      share: {
        type: Object,
        default: () => ({}),
      },
    },

    data: () => ({
      pageLoadingData: pageLoadingData,
      pullRefreshLoading: false,
      navbarHeight: 0,
      headerParallaxRate: 0,
      headerBackgroundColor: '',
    }),

    watch: {
      title: {
        immediate: true,
        handler() {
          this.updateTitle()
        },
      },
      share: {
        immediate: true,
        deep: true,
        handler() {
          this.updateWechatShareData()
        },
      },
    },

    activated() {
      this.updateTitle()
      this.updateWechatShareData()
      // 对于保活的页面，重新进入时恢复滚动位置
      this._restoreScrollPosition?.()
    },

    mounted() {
      this.bindScroll()
    },

    updated() {
      this.bindScroll()
    },

    beforeCreate() {
      initCssVar()
    },

    methods: {
      bindScroll() {
        const offScrollContainerListener = destroy => {
          this._offScrollContainerListener?.()
          if (destroy) {
            this._offScrollContainerListener = null
            this._restoreScrollPosition = null
          }
        }
        offScrollContainerListener()
        const scrollContainerEl = this.$el.querySelector(
          this.scrollContainerSelector,
        )
        if (scrollContainerEl) {
          this._offScrollContainerListener = bindEvent(scrollContainerEl)(
            'scroll',
            () => {
              const scrollTop = scrollContainerEl.scrollTop

              // 恢复滚动位置
              this._restoreScrollPosition = () => {
                scrollContainerEl.scrollTop = scrollTop
              }

              // 支持头部视差滚动
              const rate = clamp(
                this.navbarHeight
                  ? scrollTop / this.navbarHeight
                  : scrollTop
                  ? 1
                  : 0,
                0,
                1,
              )
              if (
                this.enableHeaderParallax ||
                this.getHeaderParallaxBackground ||
                this.classical ||
                this.headerBackgroundColorWhenScroll
              ) {
                this.headerParallaxRate = rate
                if (this.getHeaderParallaxBackground) {
                  this.headerBackgroundColor =
                    this.getHeaderParallaxBackground(rate)
                }
              }

              pageBridge.emit('scroll', {
                scrollTop,
                headerParallaxRate: rate,
              })
            },
          )
          this.$once('hook:beforeDestroy', () =>
            offScrollContainerListener(true),
          )
        }

        // 适配 Tab 栏
        if (!this._tabbarHeight) {
          const tabbarHeight =
            document.querySelector('.__tabbar__')?.clientHeight
          this._tabbarHeight = tabbarHeight
          tabbarHeight && setRootCssVar('tab-bar-height', `${tabbarHeight}px`)
        }

        // 派发导航栏高度等
        if (!this.navbarHeight) {
          const navbarHeight =
            this.$el.querySelector(`.${this._.hd}`)?.clientHeight || 0
          this.$emit('ready', {
            navbarHeight,
          })
          pageBridge.emit('ready', {
            pageHeaderHeight: navbarHeight,
          })

          this.navbarHeight = navbarHeight
        }
      },

      // 更新标题
      updateTitle() {
        document.title =
          this.title ||
          // 使用空白字符代替，不然某些平台下空格不会生效
          '\u200D'
      },

      // 更新微信分享数据
      updateWechatShareData() {
        const share = this.share || {}
        const hasShare = !!share.title
        const shareParams = {
          title: hasShare
            ? share.title
            : '多多商城 | 秒杀满减活动不停，大牌消费券等你来抢',
          desc: hasShare
            ? share.desc || ''
            : '秒杀满减活动不停，大牌消费券等你来抢',
          imgUrl:
            share.cover ||
            'https://cdn-resource.gogpay.cn/npm/_/1653549922Ta7KuX.jpg',
          link: share.url || location.href,
        }
        logg('设置微信分享参数', {
          多彩宝分享参数: share,
          微信分享参数: shareParams,
        })
        wechat.updateShareData(shareParams)
      },

      // 点击返回
      handleBackClick() {
        navigateBack()
      },

      // 点击分享
      async handleShareClick() {
        await dcb.share({
          ...this.share,
          url: this.share.url || location.href,
        })
      },
    },
  })
</script>

<style lang="scss" module>
  :root {
    --page-bg-color: #f5f5f5;
    --status-bar-height: 36px;
    --page-header-height: calc(var(--status-bar-height) + 40px);
    // prettier-ignore
    --tab-bar-height: 0PX;
    --bold-value: 500;
    // prettier-ignore
    --safe-area-height: 0PX;
  }

  :global {
    .iPhoneXPadding {
      box-sizing: content-box;
      padding-bottom: constant(safe-area-inset-bottom);
      padding-bottom: env(safe-area-inset-bottom);
    }

    .iPhoneXMargin {
      margin-bottom: constant(safe-area-inset-bottom);
      margin-bottom: env(safe-area-inset-bottom);
    }
  }

  .page {
    position: relative;
    min-height: 100vh;
    background-color: var(--page-bg-color);
    overflow: hidden;

    &.loading {
      background: #f5f5f5;
    }

    .hd {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: var(--page-header-height);
      padding-top: var(--status-bar-height);
      padding-left: 12px;
      padding-right: 12px;
      background-color: white;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      /* // 暂时全部去掉，UI 上并没有，没必要自找麻烦
      box-shadow: 0 0 2px 1px #e6e2e2; */
      line-height: 1;
      // y 轴上需设为 101%，否则在安卓上可能会出现白边
      background-size: 100% 101%;
      background-repeat: no-repeat;
      background-position: center;
      z-index: 1999;

      .back {
        flex-shrink: 0;
        font-size: 16px;
        margin-left: 0px;
        color: #222222;

        &:active {
          opacity: 0.8;
        }
      }

      .grow {
        flex-grow: 1;
      }

      .title {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        font-size: 18px;
        line-height: 1.1;
        font-weight: bold;
        color: #222222;
        text-align: center;
        white-space: nowrap;
        max-width: 70%;
      }

      .extra {
        flex-shrink: 0;
        display: flex;

        .share {
          font-size: 22px;

          &:active {
            opacity: 0.9;
          }
        }
      }
    }

    .placeholder {
      height: var(--page-header-height);
    }

    .bd {
      position: relative;
      height: calc(100vh - var(--page-header-height));
      transform: scale(1);

      .loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        .icon {
          width: 80px;
          height: 80px;
        }

        .tips {
          font-size: 14px;
          color: #666;
          text-align: center;
        }
      }

      .container {
        position: relative;
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        box-sizing: border-box;
        -webkit-overflow-scrolling: touch;

        .pullRefreshList {
          min-height: 100%;
        }

        > :global(.van-pull-refresh) {
          min-height: 100%;
        }
      }
    }

    &.whiteBack {
      .hd {
        .back {
          color: white;
        }
      }
    }

    &.onlyBack {
      .hd {
        pointer-events: none;

        .back {
          pointer-events: all;
        }

        .title {
          display: none;
        }

        .extra {
          display: none;
        }
      }
    }

    &.whiteBackground {
      background-color: white;
    }

    &.transparentHeader {
      .hd {
        position: absolute;
        background-color: transparent;
        box-shadow: none;
      }

      .placeholder {
        height: 0;
      }

      .bd {
        min-height: 100vh;
      }
    }

    &.noHeaderShadow {
      .hd {
        box-shadow: none;
      }
    }

    &.titleAdaptive {
      .hd {
        .title {
          position: relative;
        }
      }
    }

    &.safeArea {
      .bd {
        .container {
          padding-bottom: constant(safe-area-inset-bottom);
          padding-bottom: env(safe-area-inset-bottom);
        }
      }
    }

    &.customHeader {
      .hd {
        padding-top: 0;
        padding-left: 0;
        padding-right: 0;

        > * {
          padding-top: var(--status-bar-height);
        }
      }
    }
  }

  // 兼容 Tab 栏
  :global(.__has_tabbar__) {
    .page {
      .bd {
        height: calc(100vh - var(--page-header-height) - var(--tab-bar-height));

        .container {
          .tabbarHolder {
            height: var(--tab-bar-height);
          }
        }
      }
    }
  }

  // 兼容挂在 body 下的组件
  :global {
    body > {
      .van-popup:not(.van-popup--center, .van-popup--bottom) {
        top: var(--page-header-height);
      }

      .van-overlay.van-image-preview__overlay {
        z-index: 100000 !important;
      }

      .van-image-preview {
        z-index: 100001 !important;
      }
    }
  }
</style>
